/*
 * Yoni carousel — one horizontal, snap-scrolling track shared by the benefits,
 * ingredients and reviews sections.
 *
 * Two behaviours, both on the same real scroll container (so touch swipe,
 * trackpad and keyboard all keep working for free):
 *   - arrows: prev/next scroll by exactly one slide
 *   - auto:   a slow, continuous drift; slides are cloned once so the loop is
 *             seamless, and it pauses on hover, focus, user scroll and
 *             whenever the section is off-screen.
 *   - loop:   without the drift, the row is endless (assets/yoni-loop.js):
 *             after the last slide the first comes back. data-loop="false" opts out.
 *
 * Motion is gated by body.yoni-motion-off and prefers-reduced-motion: in those
 * cases the drift never starts, but the arrows still work.
 * Re-initialises on shopify:section:load so the theme editor stays usable.
 */
(function () {
  var RESUME_DELAY = 2500; // ms of no interaction before the drift resumes

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function motionAllowed() {
    var off = document.body.classList.contains('yoni-motion-off');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return !off && !reduce;
  }

  function init(root) {
    if (root.dataset.yoniCarouselInit === '1') return;
    root.dataset.yoniCarouselInit = '1';

    var track = root.querySelector('[data-carousel-track]');
    if (!track) return;
    var prev = root.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]');

    function slides() {
      return track.querySelectorAll(':scope > *:not([data-carousel-clone])');
    }
    function step() {
      var s = slides()[0];
      if (!s) return 320;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return s.getBoundingClientRect().width + gap;
    }

    function behavior() { return motionAllowed() ? 'smooth' : 'auto'; }
    // Arrows glide one slide on the theme's curve (yoni-loop.js), counted from where a running
    // glide is headed, so quick clicks add up instead of being swallowed.
    function move(dir) {
      pause();
      if (!window.yoniGlide) { track.scrollBy({ left: dir * step(), behavior: behavior() }); return; }
      var from = window.yoniGlide.target(track);
      window.yoniGlide(track, (from === null ? track.scrollLeft : from) + dir * step());
    }
    if (prev) prev.addEventListener('click', function () { move(-1); });
    if (next) next.addEventListener('click', function () { move(1); });

    var auto = root.getAttribute('data-autoscroll') === 'true';
    // The drift loops on its own; otherwise the row is endless unless the section opts out.
    var loop = (!auto || !motionAllowed()) && root.getAttribute('data-loop') !== 'false' && window.yoniLoop
      ? window.yoniLoop(track) : null;

    // Arrows hide when the track has nothing to scroll and, on a row that ends (no drift, no
    // loop), read as disabled at either end (aria-disabled keeps them focusable).
    var nav = (prev || next) && (prev || next).parentElement;
    function sync() {
      var max = track.scrollWidth - track.clientWidth;
      if (nav) nav.style.display = max < 40 ? 'none' : '';
      if (auto) return;
      var ends = !(loop && loop.active);
      if (prev) prev.setAttribute('aria-disabled', ends && track.scrollLeft <= 1 ? 'true' : 'false');
      if (next) next.setAttribute('aria-disabled', ends && track.scrollLeft >= max - 1 ? 'true' : 'false');
    }
    // Opt-in (data-carousel-fit-end on the track): the last stop puts a whole card at the start,
    // like every other stop, instead of leaving a sliver of a card cut at the rail's edge.
    // The section's CSS turns --carousel-end into a spacer after the last card.
    function fitEnd() {
      if (!track.hasAttribute('data-carousel-fit-end')) return;
      track.style.setProperty('--carousel-end', '0px');
      var s = slides(), max = track.scrollWidth - track.clientWidth;
      if (s.length < 2 || max < 40) return; // nothing worth scrolling: no spacer, the arrows hide (sync)
      var first = s[0].getBoundingClientRect().left;
      for (var i = 1; i < s.length; i++) {
        var stop = s[i].getBoundingClientRect().left - first;
        if (stop >= max - 2) {
          if (stop > max + 2) track.style.setProperty('--carousel-end', (stop - max) + 'px');
          return;
        }
      }
    }
    function refresh() {
      if (loop) loop.refresh();
      if (!loop || !loop.active) fitEnd(); // an endless row has no last stop
      sync();
    }
    refresh();
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('load', refresh);
    if ('ResizeObserver' in window) new ResizeObserver(refresh).observe(track);

    // A card reached with Tab scrolls fully into view instead of staying cut off at the edge.
    track.addEventListener('focusin', function (e) {
      var card = e.target.closest('[data-carousel-track] > *');
      if (card) card.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: behavior() });
    });

    // ---- continuous drift ---------------------------------------------------
    if (!auto || !motionAllowed()) return;

    var speed = parseFloat(root.getAttribute('data-speed')) || 30; // px per second
    var paused = false;
    var offscreen = false;
    var resumeTimer = null;
    var last = null;
    var rafId = null;
    var loopWidth = 0;

    // Clone the slides once so scrolling past the end lands on an identical
    // copy — the reset is then invisible. Clones are inert for a11y.
    function buildLoop() {
      track.querySelectorAll('[data-carousel-clone]').forEach(function (c) { c.remove(); });
      var originals = Array.prototype.slice.call(slides());
      if (!originals.length) return 0;
      var width = 0;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      originals.forEach(function (s) { width += s.getBoundingClientRect().width + gap; });
      // Only loop when the content actually overflows; otherwise there is
      // nothing to scroll and cloning would just duplicate visible cards.
      if (width <= track.clientWidth + 1) return 0;
      originals.forEach(function (s) {
        var c = s.cloneNode(true);
        c.setAttribute('data-carousel-clone', '');
        c.setAttribute('aria-hidden', 'true');
        c.querySelectorAll('a, button, input, select, textarea').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
        track.appendChild(c);
      });
      return width;
    }

    function pause() {
      paused = true;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () { paused = false; }, RESUME_DELAY);
    }

    // The drift position is kept here as a float: scrollLeft floors sub-pixel
    // writes, so `scrollLeft += 0.48` every frame would round to 0 and never
    // move. Accumulate precisely, then write the absolute value.
    var pos = 0;

    function frame(now) {
      if (last == null) last = now;
      var dt = (now - last) / 1000;
      last = now;
      if (paused || offscreen || loopWidth <= 0) {
        pos = track.scrollLeft; // stay in sync with arrows / manual scrolling
      } else {
        pos += speed * dt;
        if (pos >= loopWidth) pos -= loopWidth;
        track.scrollLeft = pos;
      }
      rafId = window.requestAnimationFrame(frame);
    }

    loopWidth = buildLoop();
    if (!loopWidth) return;

    ['pointerenter', 'focusin'].forEach(function (ev) {
      root.addEventListener(ev, function () { paused = true; clearTimeout(resumeTimer); });
    });
    ['pointerleave', 'focusout'].forEach(function (ev) {
      root.addEventListener(ev, function () { pause(); });
    });
    ['wheel', 'touchstart', 'pointerdown'].forEach(function (ev) {
      track.addEventListener(ev, pause, { passive: true });
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { offscreen = !e.isIntersecting; });
      }, { threshold: 0 }).observe(root);
    }

    // Slide widths are responsive — rebuild the loop when the viewport changes.
    // Guarded on the track's own width: cloning slides and late-loading fonts
    // also fire ResizeObserver, and rebuilding on those would reset the scroll
    // position on a loop and freeze the carousel.
    if ('ResizeObserver' in window) {
      var lastW = track.clientWidth;
      var t = null;
      new ResizeObserver(function () {
        if (Math.abs(track.clientWidth - lastW) < 2) return;
        lastW = track.clientWidth;
        clearTimeout(t);
        t = setTimeout(function () { pos = 0; track.scrollLeft = 0; loopWidth = buildLoop(); }, 200);
      }).observe(track);
    }

    rafId = window.requestAnimationFrame(frame);
    root.addEventListener('yoni:carousel:destroy', function () { if (rafId) cancelAnimationFrame(rafId); });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-yoni-carousel]').forEach(init);
  }

  ready(function () { initAll(document); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
